import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { FaFacebookF, FaInstagram, FaEnvelope } from "react-icons/fa";
import Navbar from "../components/NavBar";
import AppStoreLinks from "../components/AppStoreLinks";
import { Collections, CollectionsClass } from "../utils/firestore/collections";
import { handleErrors } from "../utils/firestore/handleErrors";
import { useAppSelector } from "../store/store";

const CONTACT_EMAIL = "veshje.arhax@gmail.com";
const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Kontakt — VeshjePerTy")}`;
const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61577516464829";
const INSTAGRAM_URL = "https://www.instagram.com/veshje.arhax";

const socialLinksClass =
    "inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-lg font-medium text-sm transition";

function SocialContacts({ className = "" }: { className?: string }) {
    return (
        <div className={`space-y-2 ${className}`}>
            <p className="text-sm font-medium text-gray-800">Rrjetet sociale</p>
            <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${socialLinksClass} bg-[#1877f2]/10 text-[#1877f2] hover:bg-[#1877f2]/20`}
            >
                <FaFacebookF className="text-lg" />
                Facebook
            </a>
            <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${socialLinksClass} bg-pink-500/10 text-pink-600 hover:bg-pink-500/20`}
            >
                <FaInstagram className="text-lg" />
                Instagram
            </a>
            <a
                href={mailtoHref}
                className={`${socialLinksClass} bg-[#704e37]/10 text-[#704e37] hover:bg-[#704e37]/20`}
            >
                <FaEnvelope className="text-lg" />
                Email
            </a>
        </div>
    );
}

const ContactPage = () => {
    const navigate = useNavigate();
    const { user } = useAppSelector((s) => s.auth);
    const [email, setEmail] = useState(user?.email ?? "");
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (user?.email) setEmail(user.email);
    }, [user?.email]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        setError("");
        setSuccess(false);
        const trimmedEmail = email.trim().toLowerCase();
        const trimmedTitle = title.trim();
        const trimmedMessage = message.trim();
        if (!trimmedEmail) {
            setError("Email-i është i detyrueshëm.");
            return;
        }
        if (!trimmedTitle) {
            setError("Titulli është i detyrueshëm.");
            return;
        }
        if (!trimmedMessage) {
            setError("Mesazhi është i detyrueshëm.");
            return;
        }
        setSending(true);
        const coll = collection(CollectionsClass.instance, Collections.CONTACT_MESSAGES);
        const pendingQuery = query(
            coll,
            where("email", "==", trimmedEmail),
            where("status", "==", "pending")
        );
        getDocs(pendingQuery)
            .then((snap) => {
                if (!snap.empty) {
                    setError("Keni një mesazh në pritje. Ju lutemi prisni përgjigjen tonë para se të dërgoni një të ri.");
                    setSending(false);
                    return;
                }
                return addDoc(coll, {
                    title: trimmedTitle,
                    email: trimmedEmail,
                    message: trimmedMessage,
                    status: "pending",
                    created_at: Date.now(),
                });
            })
            .then((ref) => {
                if (ref) {
                    setTitle("");
                    setMessage("");
                    setSuccess(true);
                }
            })
            .catch((e) => setError(handleErrors(e)))
            .finally(() => setSending(false));
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-[#f8f2ed] font-manrope">
                <Navbar />
                <div className="max-w-xl mx-auto px-4 py-8">
                    <h1 className="text-2xl font-semibold text-[#704e37] mb-2">Na kontaktoni</h1>
                    <p className="text-gray-600 text-sm mb-4">
                        Për të dërguar një mesazh përmes kësaj faqeje, duhet të jeni të kyçur. Përndryshe na kontaktoni drejtpërdrejt me email.
                    </p>
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-4">
                        <p className="text-gray-700 text-sm">
                            Përdorni <strong>Email</strong>, Facebook ose Instagram për të na kontaktuar. Për formularin në faqe, kyçuni.
                        </p>
                        <SocialContacts />
                        <div className="pt-4 border-t border-gray-100">
                            <AppStoreLinks />
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate("/login")}
                            className="w-full py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
                        >
                            Kyçuni për të përdorur formularin
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-xl mx-auto px-4 py-8">
                <h1 className="text-2xl font-semibold text-[#704e37] mb-2">Na kontaktoni</h1>
                <p className="text-gray-600 text-sm mb-6">
                    Dërgoni një mesazh dhe do t'ju përgjigjemi nga adresa <strong>{CONTACT_EMAIL}</strong>. Nëse keni një mesazh në pritje, nuk mund të dërgoni një të ri derisa të përgjigjemi.
                </p>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6 space-y-4">
                    <SocialContacts />
                    <div className="pt-4 border-t border-gray-100">
                        <AppStoreLinks />
                    </div>
                </div>
                {success && (
                    <div className="mb-4 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm">
                        Mesazhi u dërgua. Do t'ju përgjigjemi sa më shpejt të jetë e mundur.
                    </div>
                )}
                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email-i juaj *</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                            placeholder="email@shembull.com"
                            required
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Titulli *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                            placeholder="Titulli i mesazhit"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mesazhi *</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                            placeholder="Shkruani mesazhin tuaj..."
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={sending}
                        className="w-full py-2.5 rounded-lg bg-[#704e37] text-white font-medium hover:opacity-90 disabled:opacity-50"
                    >
                        {sending ? "Duke dërguar..." : "Dërgo mesazhin"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ContactPage;
